import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "./AuthContext";
import ReactGA from "react-ga4";
//import mixpanel from "mixpanel-browser";

const AnalyticsContext = React.createContext(null);

/* example of a second tracker that may be initialized
//
const initMixpanel = user => {
  //TODO - dynamic import?
  //const { mixpanel } = await import("mixpanel-browser");

  mixpanel.init(window.MIXPANEL_TRACKER);
  //mixpanel.set_config({ debug: true });
  if (user) {
    if (user.profile) {
      const company = user.is_staff
        ? "OpenSlate"
        : user.profile.subscription
        ? user.profile.subscription.name
        : "unknown";
      mixpanel.identify(user.profile.id);
      mixpanel.people.set("company", company);
    } else {
      mixpanel.identify(user.username);
    }

    mixpanel.people.set({
      $first_name: user.first_name,
      $last_name: user.last_name,
      $email: user.email
    });
  }

  return mixpanel;
};
*/

/**
 * The Analytics provider is designed to be a centralized way to collect data,
 * initialize multiple trackers, and send tracker to requests to the enabled trackers
 *
 *
 * @param {Array} trackers - a list of trackers to initialize for the app
 * @returns {Function} setTrackerData
 */
const AnalyticsProvider = ({ trackers = ["ga"], ...rest }) => {
  const user = useAuth();
  const enableMixpanel = trackers.includes("mixpanel");
  const enableGA = trackers.includes("ga");

  //const mixpanel = enableMixpanel ? initMixpanel(user) : {};
  const reactGA = useMemo(() => {
    if (enableGA) {
      ReactGA.initialize(window.GA_TRACKER);
      user && ReactGA.set({ userId: user.profileId });
      user && ReactGA.set({ user_properties: { company: user.company } });
      return ReactGA;
    }

    return {};
  }, [user, enableGA]);

  const [trackerData, setTrackerData] = useState({});

  /**
   * tracker data will come in in a generic format from the payload object
   * the payload will have an 'action' value, which will determine which
   * method to call on each respective analytics tracker
   */
  useEffect(() => {
    try {
      switch (trackerData.method) {
        case "pageview":
          //example of a second tracker being called
          if (enableMixpanel) {
            //mixpanel.track("Pageview", { url: trackerData.url });
          }

          //making sure user properties are being set
          user && ReactGA.set({ userId: user.profileId });
          user && ReactGA.set({ user_properties: { company: user.company } });

          if (enableGA) {
            reactGA.pageview(trackerData.url);
          }
          break;

        case "event":
          if (enableGA) {
            //making sure user properties are being set
            user && ReactGA.set({ userId: user.profileId });
            user && ReactGA.set({ user_properties: { company: user.company } });
            const event = {
              category: trackerData?.category || "User",
              action: trackerData.action,
              label: trackerData.label
            };
            if (trackerData?.value) {
              event.value = trackerData.value;
            }
            if (trackerData?.nonInteraction) {
              event.nonInteraction = trackerData.nonInteraction;
            }
            if (trackerData?.transport) {
              event.transport = trackerData.transport;
            }

            reactGA.event(event);
          }

          break;
        case "logout":
          ReactGA.set({ userId: null });
          ReactGA.set({ user_properties: { company: null } });
          break;
        default:
          break;
      }
    } catch (error) {
      console.log("analytics error:", user?.profile);
      window?.Sentry && window.Sentry.captureException(error);
    }
  }, [trackerData, user, enableMixpanel, enableGA, reactGA]);

  return <AnalyticsContext.Provider value={setTrackerData} {...rest} />;
};

const useAnalyticsProvider = () => {
  const context = React.useContext(AnalyticsContext);
  if (context === undefined || context === null) {
    throw new Error(
      `useAnalyticsProvider must be used within a AnalyticsProvider`
    );
  }
  return context;
};

export { AnalyticsProvider, useAnalyticsProvider };
